import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { seedDemoAppointments } from '../seedDemoAppointments';
import { clearDemoAppointments } from '../clearDemoAppointments';
import {
  isDemoAppointmentsToolsEnabled,
  DEMO_SEED_DATA_SOURCE,
  demoSeedDataSourceStorageKey,
} from '../constants';
import './DemoAppointmentsPanel.css';

const toDateInputValue = (date) => format(date, 'yyyy-MM-dd');

const readStoredDataSource = () => {
  try {
    const stored = window.localStorage.getItem(demoSeedDataSourceStorageKey());
    if (stored === DEMO_SEED_DATA_SOURCE.DEFAULT || stored === DEMO_SEED_DATA_SOURCE.COMPANY) {
      return stored;
    }
  } catch {
    // ignore storage errors
  }
  return DEMO_SEED_DATA_SOURCE.COMPANY;
};

const DemoAppointmentsPanel = () => {
  const { companyData, user } = useAuth();
  const isSpecialAdmin = isDemoAppointmentsToolsEnabled(user?.isSpecialAdmin);
  const { showToast } = useNotification();
  const [anchorDate, setAnchorDate] = useState(() => toDateInputValue(new Date()));
  const [dataSource, setDataSource] = useState(readStoredDataSource);
  const [busy, setBusy] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [isError, setIsError] = useState(false);

  const ownerId = companyData?.ownerId || user?.uid;

  const parsedAnchorDate = useMemo(() => {
    const [year, month, day] = anchorDate.split('-').map(Number);
    if (!year || !month || !day) return new Date();
    return new Date(year, month - 1, day, 12, 0, 0, 0);
  }, [anchorDate]);

  const useCompanyData = dataSource === DEMO_SEED_DATA_SOURCE.COMPANY;

  const handleDataSourceChange = (nextSource) => {
    setDataSource(nextSource);
    try {
      window.localStorage.setItem(demoSeedDataSourceStorageKey(), nextSource);
    } catch {
      // ignore storage errors
    }
  };

  if (!isSpecialAdmin) {
    return null;
  }

  const handleSeed = async () => {
    if (!companyData?.id || !ownerId) {
      showToast('Sign in with a company account first.', 'error');
      return;
    }

    setBusy(true);
    setIsError(false);
    setStatusMessage('Creating demo appointments…');

    try {
      const result = await seedDemoAppointments({
        companyId: companyData.id,
        ownerId,
        anchorDate: parsedAnchorDate,
        isSpecialAdmin: true,
        useCompanyData,
      });

      const dataNote = result.usingCompanyData
        ? ' Used your company services, business hours, and team schedules.'
        : ' Used generic sample services and default 9–5 hours.';

      setStatusMessage(
        `Added ${result.count} demo appointments (${result.todayCount} on the selected day). Open Calendar or Overview to screenshot.${dataNote}`
      );
      showToast('Demo appointments created', 'success');
    } catch (err) {
      console.error('[DemoAppointmentsPanel] seed failed:', err);
      setIsError(true);
      const permissionHint =
        err?.code === 'permission-denied'
          ? ' Update the database access rules if you have not yet.'
          : '';
      setStatusMessage((err.message || 'Failed to create demo appointments.') + permissionHint);
      showToast('Could not create demo appointments', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleClear = async () => {
    if (!companyData?.id) {
      showToast('Sign in with a company account first.', 'error');
      return;
    }

    setBusy(true);
    setIsError(false);
    setStatusMessage('Removing demo appointments…');

    try {
      const result = await clearDemoAppointments(companyData.id, { isSpecialAdmin: true });
      setStatusMessage(result.message);
      showToast(result.deleted > 0 ? 'Demo appointments removed' : 'Nothing to remove', 'info');
    } catch (err) {
      console.error('[DemoAppointmentsPanel] clear failed:', err);
      setIsError(true);
      setStatusMessage(err.message || 'Failed to remove demo appointments.');
      showToast('Could not remove demo appointments', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="demo-appt-panel" aria-labelledby="demo-appt-panel-title">
      <h3 id="demo-appt-panel-title" className="demo-appt-panel__title">
        Screenshot demo appointments
      </h3>
      <p className="demo-appt-panel__desc">
        Populate your calendar and overview with realistic sample bookings for marketing
        screenshots. Demo rows are tagged and can be removed in one click.
      </p>

      <div className="demo-appt-panel__row">
        <div className="demo-appt-panel__field">
          <label htmlFor="demo-appt-anchor-date">Anchor day (today&apos;s column)</label>
          <input
            id="demo-appt-anchor-date"
            type="date"
            value={anchorDate}
            onChange={(e) => setAnchorDate(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>

      <fieldset className="demo-appt-panel__data-source" disabled={busy}>
        <legend className="demo-appt-panel__data-source-legend">Appointment data</legend>
        <label className="demo-appt-panel__radio">
          <input
            type="radio"
            name="demo-appt-data-source"
            value={DEMO_SEED_DATA_SOURCE.COMPANY}
            checked={dataSource === DEMO_SEED_DATA_SOURCE.COMPANY}
            onChange={() => handleDataSourceChange(DEMO_SEED_DATA_SOURCE.COMPANY)}
          />
          <span className="demo-appt-panel__radio-text">
            <strong>My company data</strong>
            <span>Uses your services, prices, business hours, and team schedules.</span>
          </span>
        </label>
        <label className="demo-appt-panel__radio">
          <input
            type="radio"
            name="demo-appt-data-source"
            value={DEMO_SEED_DATA_SOURCE.DEFAULT}
            checked={dataSource === DEMO_SEED_DATA_SOURCE.DEFAULT}
            onChange={() => handleDataSourceChange(DEMO_SEED_DATA_SOURCE.DEFAULT)}
          />
          <span className="demo-appt-panel__radio-text">
            <strong>Default demo data</strong>
            <span>Uses generic sample services and 9–5 hours — good when you have not set up services yet.</span>
          </span>
        </label>
      </fieldset>

      <div className="demo-appt-panel__actions">
        <button
          type="button"
          className="demo-appt-panel__btn demo-appt-panel__btn--primary"
          onClick={handleSeed}
          disabled={busy}
        >
          {busy ? 'Working…' : 'Seed demo week'}
        </button>
        <button
          type="button"
          className="demo-appt-panel__btn demo-appt-panel__btn--ghost"
          onClick={handleClear}
          disabled={busy}
        >
          Clear demo data
        </button>
      </div>

      {statusMessage ? (
        <p className={`demo-appt-panel__status${isError ? ' demo-appt-panel__status--error' : ''}`}>
          {statusMessage}
        </p>
      ) : null}

      <p className="demo-appt-panel__hint">
        Tip: after seeding, go to Overview → Calendar (day/week view) or Today&apos;s appointments.
        Only special admin accounts can use this tool.
      </p>
    </section>
  );
};

export default DemoAppointmentsPanel;
